import { useState, KeyboardEvent, FormEvent } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { PlusIcon } from "@radix-ui/react-icons";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useFileUpload } from "../hooks/useFileUpload";
import { useSnippetsVersionInfo } from "@/zapi-frontend/shared/hooks/useVersionInfo";
import { TModelType, MODEL_OPTIONS } from "@/zapi-frontend/shared/types/models";
import { AppButton, AppTextarea, Typography } from "@libs/zapi-ui/custom";
import { KeyReturnIcon } from "@libs/zapi-ui/assets";
import { TrashIcon } from "@phosphor-icons/react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";

type TChatFormProps = {
  projectId: string;
  isLoading: boolean;
  onSubmit: (message: string, model: TModelType | null) => void;
  onClearChat: () => void;
  onAbortRequest: () => void;
  hasMessages: boolean;
  disabled?: boolean;
  isSendingRequest: boolean;
  isAbortingRequest: boolean;
};

export function ChatForm(props: TChatFormProps) {
  const {
    projectId,
    isLoading,
    onSubmit,
    onClearChat,
    onAbortRequest,
    hasMessages,
    disabled = false,
    isSendingRequest,
    isAbortingRequest
  } = props;
  const [input, setInput] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const { isLabs } = useMeStore();
  const { selectedProject } = useProjectStore();
  const { openFileDialog, attachmentFileInputRef, handleFileInputChange } = useFileUpload(projectId);

  const { modelName, setModelName } = useChatStore();
  const { displayText: versionDisplayText } = useSnippetsVersionInfo();

  const effectiveModel = isLabs ? modelName : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Replace shit+enter with new line
    const value = e.target.value.replace(/\u00a0/g, "\n");
    setInput(value);
  };

  // Vibecoded solution for the Japanese IME conversion (don't submit on enter)
  const handleCompositionEnd = () => {
    setTimeout(() => setIsComposing(false), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as globalThis.KeyboardEvent;
    const composing = isComposing || nativeEvent.isComposing;

    if (e.key === "Enter" && !e.shiftKey && !composing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || disabled) return;

    const messageText = input.trim();
    setInput("");
    onSubmit(messageText, effectiveModel);
  };

  const chatTextPlaceholder = (() => {
    return disabled ? "Loading chapter..." : selectedProject?.chatEnabled ? "Ask me anything..." : "Chat is disabled";
  })();

  return (
    <div className="p-[8px]" data-testid="chat-form-root">
      <div className="flex flex-col relative">
        {/* Model & Web-research controls */}
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="w-full flex items-center gap-3">
            {isLabs && (
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <Typography variant="body3" className="tracking-wide">
                    Model
                  </Typography>

                  <Select value={modelName} onValueChange={setModelName}>
                    <SelectTrigger
                      id="model-select"
                      className="h-6 text-[10px] min-w-18 gap-2 px-1"
                      data-testid="model-select-trigger"
                    >
                      <SelectValue className="" />
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col h-auto relative">
          <div className="flex flex-col gap-2 py-1 relative border border-innoveraZinc-150 rounded-[8px] transition-shadow duration-300 ease bg-white hover:ring-2 hover:ring-innoveraZinc-150 focus-within:ring-2 focus-within:ring-innoveraZinc-150">
            <AppTextarea
              fullWidth
              className="h-[80px] resize-none !border-0 !ring-0"
              placeholder={chatTextPlaceholder}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={handleCompositionEnd}
              disabled={isLoading || disabled || !selectedProject?.chatEnabled}
              rows={5}
              data-testid="chat-input"
            />
            <div className="flex gap-2 items-center justify-between px-2 pb-1">
              <div className="flex items-center gap-2">
                <AppButton
                  size="sm"
                  type="button"
                  color="zinc100"
                  onClick={openFileDialog}
                  disabled={isLoading || disabled || !selectedProject?.chatEnabled}
                  data-testid="add-files-button"
                >
                  <PlusIcon /> Add files
                </AppButton>
                {isLabs && versionDisplayText && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Typography
                        variant="body3"
                        className="text-xs text-gray-500 font-mono truncate max-w-[85px]"
                        data-testid="version-info"
                      >
                        {versionDisplayText}
                      </Typography>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Typography variant="body3">{versionDisplayText}</Typography>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <input
                type="file"
                ref={attachmentFileInputRef}
                accept=".pdf,.pptx,.jpg,.jpeg,.png,.txt,.csv,.xlsx,.xls,.xlsm,.docx"
                style={{ display: "none" }}
                multiple
                onChange={handleFileInputChange}
                data-testid="file-input"
              />

              {!isLoading && (
                <AppButton
                  className="ml-auto"
                  endAdornment={<KeyReturnIcon />}
                  size="sm"
                  type="submit"
                  disabled={isLoading || disabled || !input.trim() || !selectedProject?.chatEnabled}
                  data-testid="send-message-button"
                  isLoading={isSendingRequest}
                >
                  Submit
                </AppButton>
              )}
              {isLoading && !disabled && !isSendingRequest && (
                <AppButton
                  className="ml-auto"
                  color="red400"
                  size="sm"
                  type="button"
                  onClick={onAbortRequest}
                  disabled={disabled}
                  data-testid="abort-button"
                  isLoading={isAbortingRequest}
                >
                  Cancel
                </AppButton>
              )}
              <AppButton
                title="Clear chat"
                size="iconSm"
                type="button"
                onClick={onClearChat}
                disabled={isLoading || !hasMessages || disabled}
                className="transition-all duration-100 bg-innoveraRed-50 text-innoveraRed-400"
                startAdornment={<TrashIcon className="h-4 w-4" />}
                data-testid="clear-chat-button"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
